import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCertificateTemplate,
  getCertificateTemplateIndexFilterOptions,
  getConfigureNewCompetitionInPrintCertificatesFilter,
} from "../../functions/getData";
import Loader from "../general/Loader";
import LoadingBackdrop from "../general/LoadingBackdrop";
import NunitoText from "../general/NunitoText";
import ReusableButton from "../general/ReusableButton";

export default function AddNewCompetitionForCertificateModal({
  approveFunction,
  cancelApprove,
  editMode = false,
  configId = null,
}) {
  const [configurationName, setConfigurationName] = useState("");
  const [roundType, setRoundType] = useState(0);
  const [competition, setCompetition] = useState("");
  const [country, setCountry] = useState(null);
  const [organization, setOrganization] = useState([]);
  const [awardConfigurations, setAwardConfigurations] = useState([
    {
      id: 0,
      awards: [],
      selectedFile: null,
      file_id: null,
      showDisclaimer: false,
    },
  ]);
  const fileInputRefsMap = useRef(new Map([[0, { current: null }]]));
  const [awardsOptionsMap, setAwardsOptionsMap] = useState({});
  const [loadingAwardsOptionsMap, setLoadingAwardsOptionsMap] = useState({});
  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [loadingCompetitionOptions, setLoadingCompetitionOptions] =
    useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountryOptions, setLoadingCountryOptions] = useState(false);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [loadingOrganizationOptions, setLoadingOrganizationOptions] =
    useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const getFilterOptions = (getParam, additionalParams = {}) =>
    editMode
      ? getCertificateTemplateIndexFilterOptions(getParam, additionalParams)
      : getConfigureNewCompetitionInPrintCertificatesFilter(
          getParam,
          additionalParams
        );

  const extractFilterList = (data) =>
    Array.isArray(data)
      ? data
      : data?.data ?? data?.results ?? data?.items ?? [];

  const getRoles = async () => {
    const raw = await getFilterOptions("roles");
    const list = extractFilterList(raw);
    return list.map((item) => ({
      label: item.filter_name ?? item.name,
      value: item.filter_id ?? item.id,
    }));
  };

  const getCompetitions = async () => {
    const raw = await getFilterOptions("competition", {
      is_team_round: roundType,
    });
    const list = extractFilterList(raw);
    return list.map((item) => ({
      label: item.filter_name ?? item.name,
      value: item.filter_id ?? item.id,
    }));
  };

  const getCountries = async () => {
    try {
      const raw = await getFilterOptions("country", {
        round_id: competition?.value,
        is_team_round: roundType,
      });
      const list = extractFilterList(raw);
      return list.map((item) => ({
        label: item.filter_name ?? item.name,
        value: item.filter_id ?? item.id,
      }));
    } catch {
      return [];
    }
  };

  const getOrganizations = async () => {
    try {
      const raw = await getFilterOptions("organization", {
        round_id: competition?.value,
        country_id: country?.value,
        is_team_round: roundType,
      });
      const list = extractFilterList(raw);
      return list.map((item) => ({
        label: item.filter_name ?? item.name,
        value: item.filter_id ?? item.id,
      }));
    } catch {
      return [];
    }
  };

  const getAwards = async (param, configId) => {
    const raw = await getFilterOptions(param, {
      round_id: competition?.value,
      is_team_round: roundType,
    });
    const list = extractFilterList(raw);
    const mappedData = list.map((item) => ({
      label:
        typeof item === "string"
          ? item
          : item?.filter_name ?? item?.name ?? item,
      value:
        typeof item === "string" ? item : item?.filter_id ?? item?.id ?? item,
    }));

    const allSelectedAwards = awardConfigurations.flatMap(
      (config) => config.awards
    );

    return mappedData.filter(
      (award) =>
        !allSelectedAwards.some((selected) => selected.value === award.value)
    );
  };

  const handleChangeRole = (roleId) => {
    setSelectedRoles((prev) => {
      if (prev.includes(roleId)) {
        return prev.filter((id) => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const handleChangeCompetition = (event, value) => {
    setCompetition(value);
    setCountry(null);
    setOrganization([]);
    setAwardConfigurations([
      {
        id: 0,
        awards: [],
        selectedFile: null,
        file_id: null,
        showDisclaimer: false,
      },
    ]);
    setAwardsOptionsMap({});
    setLoadingAwardsOptionsMap({});
    fileInputRefsMap.current.forEach((ref) => {
      if (ref.current) ref.current.value = null;
    });
    fileInputRefsMap.current.clear();
    fileInputRefsMap.current.set(0, { current: null });
  };

  const handleChangeAwards = (configId, event, value) => {
    const newAwards = typeof value === "string" ? value.split(",") : value;

    // Update the configuration and refresh options for other configurations
    setAwardConfigurations((prev) => {
      const updatedConfigurations = prev.map((config) =>
        config.id === configId
          ? {
              ...config,
              awards: newAwards,
            }
          : config
      );

      // Refresh options for all other configurations to exclude newly selected awards
      setAwardsOptionsMap((optionsMap) => {
        const updatedMap = { ...optionsMap };
        prev.forEach((config) => {
          if (config.id !== configId && updatedMap[config.id]) {
            // Get all awards selected across all configurations (with the new selection)
            const allSelectedAwards = [
              ...updatedConfigurations
                .filter((c) => c.id !== configId)
                .flatMap((c) => c.awards),
              ...newAwards,
            ];
            // Filter out awards that are selected in any configuration
            updatedMap[config.id] = updatedMap[config.id].filter(
              (award) =>
                !allSelectedAwards.some(
                  (selected) => selected.value === award.value
                )
            );
          }
        });
        return updatedMap;
      });

      return updatedConfigurations;
    });
  };

  const handleFileChange = (configId, e) => {
    if (e.target.files[0]) {
      if (e.target.files[0].type !== "application/pdf") {
        alert("Please upload a PDF file");
        e.target.value = null;
        return;
      }
      setAwardConfigurations((prev) =>
        prev.map((config) =>
          config.id === configId
            ? { ...config, selectedFile: e.target.files[0] }
            : config
        )
      );
    }
  };

  const addAwardConfiguration = () => {
    const newId = Math.max(...awardConfigurations.map((c) => c.id), -1) + 1;
    if (!fileInputRefsMap.current.has(newId)) {
      fileInputRefsMap.current.set(newId, { current: null });
    }
    setAwardConfigurations((prev) => [
      ...prev,
      {
        id: newId,
        awards: [],
        selectedFile: null,
        file_id: null,
        showDisclaimer: false,
      },
    ]);
  };

  const removeAwardConfiguration = (configId) => {
    if (awardConfigurations.length === 1) {
      alert("At least one awards configuration is required");
      return;
    }
    fileInputRefsMap.current.delete(configId);
    setAwardConfigurations((prev) =>
      prev.filter((config) => config.id !== configId)
    );
    // Also remove from awardsOptionsMap and loadingAwardsOptionsMap
    setAwardsOptionsMap((prev) => {
      const newMap = { ...prev };
      delete newMap[configId];
      return newMap;
    });
    setLoadingAwardsOptionsMap((prev) => {
      const newMap = { ...prev };
      delete newMap[configId];
      return newMap;
    });
  };

  useEffect(() => {
    const getRoleData = async () => {
      setLoadingRoles(true);
      const filterData = await getRoles();
      setRoleOptions(filterData || []);
      setLoadingRoles(false);
    };
    getRoleData();
  }, []);

  const fetchAndPopulateConfig = useCallback(async () => {
    if (!editMode || !configId) return;
    setLoadingConfig(true);
    try {
      const data = await getCertificateTemplate(configId);
      const api = data?.data ?? data;
      setConfigurationName(api.name ?? api.configuration_name ?? "");
      const isTeam = api.is_team_round ?? api.round_type;
      const roundTypeVal =
        isTeam === true ||
        isTeam === 1 ||
        isTeam === "1" ||
        (typeof isTeam === "string" &&
          String(isTeam).toLowerCase().includes("team"))
          ? 1
          : 0;
      setRoundType(roundTypeVal);
      const roundId = api.round_id ?? api.competition_id ?? api.id;
      const compName = api.competition_name ?? api.round_name ?? "";
      if (roundId != null) {
        const compOptions = await getCertificateTemplateIndexFilterOptions(
          "competition",
          { is_team_round: roundTypeVal }
        );
        const compList = extractFilterList(compOptions);
        const match = compList.find(
          (c) =>
            (c.filter_id ?? c.id ?? c.value) === roundId ||
            (c.filter_id ?? c.id) == roundId
        );
        setCompetitionOptions(
          compList.map((c) => ({
            label: c.filter_name ?? c.name,
            value: c.filter_id ?? c.id,
          }))
        );
        setCompetition({
          label: match?.filter_name ?? match?.name ?? compName,
          value: roundId,
        });
      }
      if (api.countries?.length && roundId != null) {
        try {
          const countryData = await getCertificateTemplateIndexFilterOptions(
            "country",
            {
              round_id: roundId,
              is_team_round: roundTypeVal,
            }
          );
          const countryList = extractFilterList(countryData);
          setCountryOptions(
            countryList.map((c) => ({
              label: c.filter_name ?? c.name ?? "",
              value: c.filter_id ?? c.id,
            }))
          );
          const c = api.countries[0];
          const countryId = typeof c === "object" ? c.id ?? c.value : c;
          const countryMatch = countryList.find(
            (x) => (x.filter_id ?? x.id) == countryId
          );
          const countryLabel =
            countryMatch?.filter_name ??
            countryMatch?.name ??
            (typeof c === "object"
              ? c?.name ??
                c?.label ??
                c?.filter_name ??
                String(c?.id ?? c?.value ?? "")
              : String(c));
          setCountry({ value: countryId, label: countryLabel });
        } catch {
          const c = api.countries[0];
          const countryId = typeof c === "object" ? c.id ?? c.value : c;
          const countryLabel =
            typeof c === "object"
              ? c?.name ??
                c?.label ??
                c?.filter_name ??
                String(c?.id ?? c?.value ?? "")
              : String(c);
          setCountry({ value: countryId, label: countryLabel });
        }
      } else if (api.country_id != null || api.country_name != null) {
        setCountry({
          label: api.country_name ?? "",
          value: api.country_id ?? api.country,
        });
      }
      if (
        (api.organizations?.length || api.organization_ids?.length) &&
        roundId != null
      ) {
        try {
          const orgIds = api.organizations ?? api.organization_ids ?? [];
          const countryIdForOrg = api.countries?.[0];
          const cid =
            countryIdForOrg != null
              ? typeof countryIdForOrg === "object"
                ? countryIdForOrg?.id ?? countryIdForOrg?.value
                : countryIdForOrg
              : api.country_id;
          const orgData = await getCertificateTemplateIndexFilterOptions(
            "organization",
            {
              round_id: roundId,
              country_id: cid,
              is_team_round: roundTypeVal,
            }
          );
          const orgList = extractFilterList(orgData);
          setOrganizationOptions(
            orgList.map((o) => ({
              label: o.filter_name ?? o.name ?? "",
              value: o.filter_id ?? o.id,
            }))
          );
          const orgs = orgIds.map((o, i) => {
            const id = typeof o === "object" ? o.id ?? o.value : o;
            const match = orgList.find((x) => (x.filter_id ?? x.id) == id);
            const label =
              match?.filter_name ??
              match?.name ??
              (typeof o === "object"
                ? o?.name ??
                  o?.label ??
                  o?.filter_name ??
                  String(o?.id ?? o?.value ?? "")
                : null) ??
              api.organization_names?.[i] ??
              String(id);
            return { value: id, label };
          });
          setOrganization(orgs);
        } catch {
          const orgIds = api.organizations ?? api.organization_ids ?? [];
          const orgs = orgIds.map((o, i) => {
            const id = typeof o === "object" ? o.id ?? o.value : o;
            const label =
              typeof o === "object"
                ? o?.name ??
                  o?.label ??
                  o?.filter_name ??
                  String(o?.id ?? o?.value ?? "")
                : null;
            return {
              value: id,
              label: label ?? api.organization_names?.[i] ?? String(id),
            };
          });
          setOrganization(orgs);
        }
      }
      const roleIds = api.roles ?? [];
      if (roleIds.length) {
        setSelectedRoles(
          roleIds.map((r) => (typeof r === "object" ? r.id ?? r.value : r))
        );
      } else if (api.allowed_roles) {
        setSelectedRoles(
          String(api.allowed_roles)
            .split(",")
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => !isNaN(n))
        );
      }
      const awards = api.awards ?? api.templates ?? [];
      if (awards.length) {
        const grouped = {};
        for (const a of awards) {
          const path = a.path ?? a.file_id ?? a.id;
          if (!grouped[path]) {
            grouped[path] = {
              path,
              file_name: a.file_name ?? "template.pdf",
              showDisclaimer: a.show_disclaimer ?? false,
              download_url: a.download_url ?? a.url,
              awards: [],
            };
          }
          const awardName = a.award ?? a;
          grouped[path].awards.push({
            label: typeof awardName === "string" ? awardName : awardName?.name,
            value: typeof awardName === "string" ? awardName : awardName?.value,
          });
          if ((a.download_url ?? a.url) && !grouped[path].download_url) {
            grouped[path].download_url = a.download_url ?? a.url;
          }
        }
        const configs = Object.values(grouped).map((g, idx) => ({
          id: idx,
          awards: g.awards,
          selectedFile: null,
          file_id: g.path,
          file_name: g.file_name,
          showDisclaimer: g.showDisclaimer,
          download_url: g.download_url,
          url: g.download_url,
        }));
        configs.forEach((c, i) => {
          if (!fileInputRefsMap.current.has(c.id)) {
            fileInputRefsMap.current.set(c.id, { current: null });
          }
        });
        setAwardConfigurations(configs);
      }
    } catch (err) {
      console.error("Failed to load configuration:", err);
      alert(err?.message || "Failed to load configuration");
    } finally {
      setLoadingConfig(false);
    }
  }, [editMode, configId]);

  useEffect(() => {
    fetchAndPopulateConfig();
  }, [fetchAndPopulateConfig]);

  if (loadingRoles || loadingConfig) {
    return <LoadingBackdrop loading={loadingRoles || loadingConfig} />;
  }

  return (
    <Box
      className="popUpModal"
      style={{
        width: 1000,
        minHeight: 365,
        maxHeight: 900,
        padding: "0 20px",
        gap: "16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowY: "scroll",
      }}
    >
      <NunitoText
        value={editMode ? "EDIT CONFIGURATION" : "ADD NEW COMPETITION"}
        fontSize={30}
        fontWeight={600}
        color="#5E75C3"
        marginTop={30}
        marginBottom={20}
      />
      <TextField
        label="Configuration Name"
        required
        fullWidth
        value={configurationName}
        onChange={(e) => setConfigurationName(e.target.value)}
        sx={{ width: 900, marginBottom: 2 }}
      />

      <FormControl
        style={{
          width: 900,
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
        }}
      >
        <FormLabel
          id="role-checkboxes-group-label"
          style={{ fontSize: 18, marginRight: 20, marginTop: 8 }}
        >
          Role:
        </FormLabel>
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox checked={true} disabled />}
            label="Admin"
          />
          {roleOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={selectedRoles.includes(option.value)}
                  onChange={() => handleChangeRole(option.value)}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
      </FormControl>

      <FormControl
        style={{
          width: 900,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        {/* BACKGROUND OPTION */}
        <FormLabel
          id="demo-radio-buttons-group-label"
          style={{ fontSize: 18, marginRight: 20 }}
        >
          Rount Type:
        </FormLabel>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          defaultValue="0"
          name="radio-buttons-group"
          row
          value={String(roundType)}
          onChange={(e) => setRoundType(Number(e.target.value))}
        >
          <FormControlLabel
            value="0"
            control={<Radio />}
            label="Individual Round"
          />
          <FormControlLabel value="1" control={<Radio />} label="Team Round" />
        </RadioGroup>
      </FormControl>

      {/* COMPETITION */}
      <Autocomplete
        aria-required
        disablePortal
        isOptionEqualToValue={(option, value) =>
          (option?.value ?? option) === (value?.value ?? value)
        }
        onOpen={async () => {
          setLoadingCompetitionOptions(true);
          setCompetitionOptions([]);
          const compData = await getCompetitions();
          setCompetitionOptions(compData);
          setLoadingCompetitionOptions(false);
        }}
        onChange={handleChangeCompetition}
        options={
          competition &&
          !competitionOptions.some(
            (o) => (o?.value ?? o) === (competition?.value ?? competition)
          )
            ? [competition, ...competitionOptions]
            : competitionOptions
        }
        sx={{ width: 900 }}
        noOptionsText={
          loadingCompetitionOptions ? (
            <Loader height={{ height: 32 }} />
          ) : (
            "No options"
          )
        }
        getOptionLabel={(o) =>
          o && typeof o === "object"
            ? o.label ?? String(o.value ?? o.name ?? "")
            : String(o ?? "")
        }
        renderInput={(params) => (
          <TextField {...params} label="Competition" required />
        )}
        value={competition}
      />

      {/* COUNTRY (optional) */}
      <Autocomplete
        disablePortal
        isOptionEqualToValue={(option, value) =>
          (option?.value ?? option) === (value?.value ?? value)
        }
        onOpen={async () => {
          setLoadingCountryOptions(true);
          setCountryOptions([]);
          const data = await getCountries();
          setCountryOptions(data);
          setLoadingCountryOptions(false);
        }}
        onChange={(e, v) => setCountry(v)}
        options={
          country &&
          !countryOptions.some(
            (o) => (o?.value ?? o) === (country?.value ?? country)
          )
            ? [country, ...countryOptions]
            : countryOptions
        }
        getOptionLabel={(o) =>
          o && typeof o === "object"
            ? o.label ?? String(o.value ?? o.name ?? "")
            : String(o ?? "")
        }
        sx={{ width: 900 }}
        noOptionsText={
          loadingCountryOptions ? (
            <Loader height={{ height: 32 }} />
          ) : (
            "No options"
          )
        }
        renderInput={(params) => (
          <TextField {...params} label="Country (optional)" />
        )}
        value={country}
        disabled={!competition}
      />

      {/* ORGANIZATION (optional) */}
      <Autocomplete
        multiple
        disablePortal
        isOptionEqualToValue={(option, value) =>
          (option?.value ?? option) === (value?.value ?? value)
        }
        onOpen={async () => {
          setLoadingOrganizationOptions(true);
          setOrganizationOptions([]);
          const data = await getOrganizations();
          setOrganizationOptions(data || []);
          setLoadingOrganizationOptions(false);
        }}
        onChange={(e, v) => setOrganization(Array.isArray(v) ? v : [])}
        options={
          organization?.length
            ? [
                ...organization.filter(
                  (org) =>
                    !organizationOptions.some(
                      (o) => (o?.value ?? o) === (org?.value ?? org)
                    )
                ),
                ...organizationOptions,
              ]
            : organizationOptions
        }
        getOptionLabel={(o) =>
          o && typeof o === "object"
            ? o.label ?? String(o.value ?? o.name ?? "")
            : String(o ?? "")
        }
        filterSelectedOptions
        sx={{ width: 900 }}
        noOptionsText={
          loadingOrganizationOptions ? (
            <Loader height={{ height: 32 }} />
          ) : (
            "No options"
          )
        }
        renderInput={(params) => (
          <TextField {...params} label="Organization (optional)" />
        )}
        value={organization}
        disabled={!competition}
      />

      {awardConfigurations.map((config, index) => {
        // Ensure we have a ref for this configuration
        if (!fileInputRefsMap.current.has(config.id)) {
          fileInputRefsMap.current.set(config.id, { current: null });
        }
        const fileInputRef = fileInputRefsMap.current.get(config.id);

        return (
          <Box
            key={config.id}
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: 900,
              position: "relative",
              gap: 20,
              marginBottom: 10,
            }}
          >
            {/* AWARDS TO BE PRINTED */}
            <Autocomplete
              multiple
              id={`tags-outlined-${config.id}`}
              sx={{ width: 440 }}
              options={awardsOptionsMap[config.id] || []}
              getOptionLabel={(option) => option.label}
              onChange={(event, value) =>
                handleChangeAwards(config.id, event, value)
              }
              onOpen={async () => {
                setLoadingAwardsOptionsMap((prev) => ({
                  ...prev,
                  [config.id]: true,
                }));
                setAwardsOptionsMap((prev) => ({ ...prev, [config.id]: [] }));
                const awardsData = await getAwards("awards", config.id);
                setAwardsOptionsMap((prev) => ({
                  ...prev,
                  [config.id]: awardsData,
                }));
                setLoadingAwardsOptionsMap((prev) => ({
                  ...prev,
                  [config.id]: false,
                }));
              }}
              filterSelectedOptions={true}
              noOptionsText={
                loadingAwardsOptionsMap[config.id] ? (
                  <Loader height={{ height: 32 }} />
                ) : (
                  "No options"
                )
              }
              renderInput={(params) => (
                <TextField {...params} label="Awards to be printed" />
              )}
              value={config.awards}
              disabled={!competition}
            />
            <FormControl
              style={{
                width: 260,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(config.showDisclaimer)}
                    onChange={(e) => {
                      setAwardConfigurations((prev) =>
                        prev.map((c) =>
                          c.id === config.id
                            ? { ...c, showDisclaimer: e.target.checked }
                            : c
                        )
                      );
                    }}
                  />
                }
                label="Show Disclaimer"
                labelPlacement="end"
                style={{
                  marginLeft: 0,
                }}
              />
            </FormControl>
            <Box
              sx={{
                width: 400,
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <input
                  accept="application/pdf"
                  style={{ display: "none" }}
                  id={`raised-button-file-${config.id}`}
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileChange(config.id, e)}
                />
                <ReusableButton
                  text={
                    config.selectedFile
                      ? config.selectedFile.name
                      : config.file_id && editMode
                      ? `${
                          config.file_name || "Current file"
                        } (re-upload to replace)`
                      : "Select PDF Template"
                  }
                  bgColor="#5E75C3"
                  fontSize={16}
                  height={55}
                  width="100%"
                  onClick={() => fileInputRef.current?.click()}
                />
              </Box>
              {editMode && (config.file_id || config.download_url || config.url) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "6px",
                    border: "2px solid #5E75C3",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() =>
                    window.open(
                      config.download_url || config.url,
                      "_blank"
                    )
                  }
                >
                  <DownloadIcon sx={{ color: "#5E75C3" }} />
                </Box>
              )}
              {awardConfigurations.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "6px",
                    border: "2px solid #E83042",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => removeAwardConfiguration(config.id)}
                >
                  <CloseIcon sx={{ color: "#E83042" }} />
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      <Box
        style={{
          display: "flex",
          justifyContent: "flex-start",
          width: 700,
          marginBottom: 16,
        }}
      >
        <Box
          onClick={addAwardConfiguration}
          style={{
            color: "#2f54eb",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: 15,
            fontWeight: 400,
            display: "flex",
            alignItems: "center",
            // gap: 4,
          }}
        >
          <AddIcon sx={{ color: "#2f54eb" }} />
          <span>Add more awards configuration</span>
        </Box>
      </Box>

      <Grid
        container
        justifyContent="space-evenly"
        style={{ marginBlock: "30px 20px" }}
      >
        <ReusableButton
          text="Cancel"
          bgColor="#8D8D8D"
          fontSize={25}
          height={63}
          width={280}
          onClick={() => cancelApprove()}
        />
        <ReusableButton
          text="Confirm"
          bgColor="#5E75C3"
          fontSize={25}
          height={63}
          width={280}
          onClick={() => {
            if (!configurationName?.trim()) {
              alert("Please enter a configuration name");
              return;
            }
            if (!competition) {
              alert("Please select a competition");
              return;
            } else {
              const hasInvalidConfig = awardConfigurations.some(
                (config) =>
                  !config.awards ||
                  config.awards.length === 0 ||
                  (!config.selectedFile && !config.file_id)
              );

              if (hasInvalidConfig) {
                alert(
                  "Please ensure all award configurations have awards selected and a PDF file uploaded (or use existing file when editing)"
                );
                return;
              }

              const configurations = awardConfigurations.map((config) => ({
                awards: config.awards.map((award) =>
                  typeof award === "object" ? award.value : award
                ),
                selectedFile: config.selectedFile || undefined,
                file_id: config.file_id || undefined,
                file_name: config.file_name || undefined,
                showDisclaimer: config.showDisclaimer,
              }));

              approveFunction({
                configuration_name: configurationName.trim(),
                is_team_round: roundType,
                round_id: competition?.value,
                country_id: country?.value ?? undefined,
                organization_ids: organization?.length
                  ? organization.map((o) => o.value)
                  : undefined,
                configurations,
                allowed_roles: selectedRoles.join(","),
              });
            }
          }}
        />
      </Grid>
    </Box>
  );
}
